import {
  Modal,
  Button,
  Box,
  Text,
  createStyles,
  Select,
  TextInput,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import swal from "sweetalert";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { editPayer, getpayer } from "../../services/payer";
import { stateZipCodes } from "../../utils/zipCodes";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { AddBillingProviderEnrollment, editProvider, getBillingProvider } from "../../services/billingprovider";
import { searchAddress, getAddressByPlaceId } from "../../services/dashboard";

// Design tokens mirrored from the billing redesign spec
const T = {
  surface: "#FFFFFF",
  surfaceAlt: "#F4F4F5",
  line: "#E5E5E5",
  lineSoft: "#F1F1F1",
  ink: "#171717",
  ink2: "#262626",
  muted: "#667085",
  accent: "#FFBA45",
  accentHover: "#EEAA33",
  accentDeep: "#B7791F",
  danger: "#DC2626",
};

const useStyles = createStyles(() => ({
  modalContent: {
    borderRadius: 12,
    overflow: "hidden",
  },
  modalBody: {
    padding: 0,
  },
  head: {
    padding: "22px 28px",
    borderBottom: `1px solid ${T.lineSoft}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  eyebrow: {
    margin: "0 0 6px",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: T.muted,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: T.ink,
    letterSpacing: "-0.018em",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: T.muted,
  },
  closeBtn: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 8,
    background: T.surfaceAlt,
    border: 0,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    color: T.ink2,
    flexShrink: 0,
    "&:hover": { background: "#E4E4E7" },
    "& svg": { width: 18, height: 18 },
  },
  body: {
    padding: "22px 28px",
    overflowY: "auto",
    maxHeight: "calc(92vh - 201px)",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: 16,
    fontWeight: 600,
    color: T.ink,
  },
  sectionTitleSpaced: {
    marginTop: 22,
  },
  grid: {
    display: "grid",
    gap: "16px 18px",
  },
  fieldLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: T.ink2,
    marginBottom: 6,
  },
  req: {
    color: T.danger,
  },
  foot: {
    padding: "8px 23px 21px",
    borderTop: `1px solid ${T.lineSoft}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },
  ghostBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    background: "transparent",
    color: T.ink2,
    border: `1px solid ${T.line}`,
    "&:hover": { background: T.surfaceAlt },
  },
  accentBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    background: T.accent,
    color: T.ink,
    border: "1px solid transparent",
    "&:hover": { background: T.accentHover },
  },
}));

// Shared input visual matching `.field input/select` from the redesign
const inputStyles = {
  input: {
    height: 42,
    minHeight: 42,
    padding: "0 12px",
    border: `1px solid ${T.line}`,
    background: T.surface,
    borderRadius: 8,
    fontSize: 13,
    color: T.ink,
    "&:focus": {
      borderColor: T.ink,
      boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
    },
  },
};

const Field = ({ classes, label, required, children }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <Text className={classes.fieldLabel}>
      {label} {required && <span className={classes.req}>*</span>}
    </Text>
    {children}
  </div>
);

const ProviderEnrollment = ({
  EditProviderEnrollmentModalOpen,
  setEditProviderEnrollmentrModalOpen,
  item,
  isPreview,
}) => {
  const { classes, cx } = useStyles();

  const { allpayer } = useSelector((state) => state.payer);
  const { profile } = useSelector((state) => state?.profile);

  const [loading, setLoading] = useState(false);
  const [iframeurl, setIFrameUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);
  const [addr2Suggestions, setAddr2Suggestions] = useState([]);
  const [showSuggestions2, setShowSuggestions2] = useState(false);
  const [addr2Loading, setAddr2Loading] = useState(false);
  const debounceRef2 = useRef(null);

  useEffect(() => {
    if (iframeurl) {
      const timeout = setTimeout(() => {
        if (!loaded) {
          window.open(iframeurl, "_blank");
        }
      }, 6000);

      return () => clearTimeout(timeout);
    }
  }, [loaded, iframeurl]);

  const form = useForm({
    initialValues: {
      prov_name_f: (item?.name || "").split(" ")[0] || "",
      prov_name_m: (item?.name || "").split(" ")[1] || "",
      prov_name_l: (item?.name || "").split(" ")[2] || "",
      payerid: "",
      prov_npi: (() => { const bi = item?.billingIdentifier; if (!bi) return ""; return typeof bi === "object" ? (bi.type === "NPI" ? bi.number || "" : "") : bi; })(),
      prov_id: (() => { const bi = item?.billingIdentifier; if (!bi || typeof bi !== "object") return ""; return bi.type !== "NPI" ? bi.number || "" : ""; })(),

      prov_taxid: item?.federalTaxID?.number || "208655599",

      prov_addr_1: item?.address || "",
      prov_addr_2: item?.address2 || "",
      prov_city: item?.city || "",
      prov_state: item?.state || "",
      prov_zip: item?.zipcode || "",
    },
  });

  useEffect(() => {
    getpayer();
    setIFrameUrl(null);
  }, []);

  useEffect(() => {
    if (EditProviderEnrollmentModalOpen) getpayer();
  }, [EditProviderEnrollmentModalOpen]);

  useEffect(() => {
    form.setValues({
      prov_name_f: (item?.name || "").split(" ")[0] || "",
      prov_name_m: (item?.name || "").split(" ")[1] || "",
      prov_name_l: (item?.name || "").split(" ")[2] || "",
      prov_npi: (() => { const bi = item?.billingIdentifier; if (!bi) return ""; return typeof bi === "object" ? (bi.type === "NPI" ? bi.number || "" : "") : bi; })(),
      prov_id: (() => { const bi = item?.billingIdentifier; if (!bi || typeof bi !== "object") return ""; return bi.type !== "NPI" ? bi.number || "" : ""; })(),

      prov_taxid: item?.federalTaxID?.number || "208655599",
      prov_addr_1: item?.address || "",
      prov_addr_2: item?.address2 || "",
      prov_city: item?.city || "",
      prov_state: item?.state || "",
      prov_zip: item?.zipcode || "",
    });
  }, [item]);

  const handleAddressSearch = (value) => {
    form.setFieldValue("prov_addr_1", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddress(value);
        setAddressSuggestions(results || []);
        setShowSuggestions(true);
      } catch (e) {
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 400);
  };

  const handleAddress2Search = (value) => {
    form.setFieldValue("prov_addr_2", value);
    if (debounceRef2.current) clearTimeout(debounceRef2.current);
    if (!value || value.length < 3) { setAddr2Suggestions([]); setShowSuggestions2(false); return; }
    debounceRef2.current = setTimeout(async () => {
      setAddr2Loading(true);
      try {
        const results = await searchAddress(value);
        setAddr2Suggestions(results || []);
        setShowSuggestions2(true);
      } catch (e) { setAddr2Suggestions([]); }
      setAddr2Loading(false);
    }, 400);
  };

  const handleSelectAddress2 = (suggestion) => {
    form.setFieldValue("prov_addr_2", suggestion.formatted_address);
    setShowSuggestions2(false);
  };

  const handleSelectAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddressLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      let streetNumber = "", route = "", city = "", addrState = "", zipcode = "";
      components.forEach((comp) => {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) addrState = comp.short_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      });
      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      form.setFieldValue("prov_addr_1", streetAddress || suggestion.formatted_address);
      if (city) form.setFieldValue("prov_city", city);
      if (addrState) form.setFieldValue("prov_state", addrState);
      if (zipcode) form.setFieldValue("prov_zip", zipcode);
    } catch (e) {
      form.setFieldValue("prov_addr_1", suggestion.formatted_address);
    }
    setAddressLoading(false);
  };

  const onSave = async () => {
    let values = form.values;
    let missingFields = [];
    const optionalFields = [
      "prov_name_f",
      "prov_name_m",
      "prov_name_l",
      "prov_npi",
      "prov_id",
      "prov_addr_1",
      "prov_addr_2",
      "prov_city",
      "prov_state",
      "prov_zip",
    ];
    Object.entries(values)
      .filter(([key, _]) => !optionalFields.includes(key))
      .forEach(([key, value]) => !value && missingFields.push(key));

    if (missingFields.length > 0) {
      await swal({
        title: "Please fill out the following fields",
        text: missingFields.join(", "),
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    const selectedState = values.prov_state?.trim();
    const enteredZipCode = values.prov_zip?.trim();

    if (selectedState && enteredZipCode) {
      const stateZipCodeEntries = stateZipCodes.filter(
        (entry) => entry.ste_name === selectedState
      );

      const isZipValid = stateZipCodeEntries.some(
        (entry) => entry.zip_code === enteredZipCode
      );

      if (!isZipValid) {
        await swal({
          title: "Invalid Zip Code",
          text: `The zip code you entered is not valid for ${selectedState}. Please enter a valid zip code.`,
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
    }

    const providerlast = values.prov_name_l?.trim();
    const providernpi = values.prov_npi?.trim();

    if (!providernpi && !providerlast) {
      await swal({
        title: "MIssing Fields",
        text: `As Per Requirements atleast one things NPI Or Last name is required`,
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    let fullName = `${values.prov_name_f} ${values.prov_name_m} ${values.prov_name_l}`;

    if (
      item?.name.trim() !== fullName?.trim() ||
      item?.federalTaxID?.number !== values.prov_taxid ||
      item?.address !== values?.prov_addr_1 ||
      item?.address2 !== values?.prov_addr_2 ||
      item?.city !== values?.prov_city ||
      item?.state !== values?.prov_state ||
      item?.zipcode !== values?.prov_zip ||
      (() => { const bi = item?.billingIdentifier; const num = bi ? (typeof bi === "object" ? bi?.number || "" : bi) : ""; return num !== values.prov_npi; })()
    ) {
      let newproviderpayload = {
        ...item,
        name: fullName,
        federalTaxID: {
          type: item?.federalTaxID?.type,
          number: values.prov_taxid,
        },
        billingIdentifier: values.prov_npi,
        serviceCode: item?.serviceCode.id,
        facilityId: item?.facilityId.id,
        createdBy: profile.user.id,
        admin: profile?.user?.admin ?? profile?.user?.id,
        address: values?.prov_addr_1,
        address2: values?.prov_addr_2,
        city: values?.prov_city,
        state: values?.prov_state,
        zipcode: values?.prov_zip,
        id: item.id,
      };

      await editProvider({
        ...newproviderpayload,
      });
    }

    let payload = {
      ...values,
      enroll_type: "1500",
      id: item.id,
    };

    const res = await AddBillingProviderEnrollment(payload);

    if (res?.data?.billingProviderEnrollment?.link) {
      const url = res.data.billingProviderEnrollment.link[0].url;
      window.open(url, "_blank");
    }

    setLoading(false);
    await getBillingProvider({ page: 1 });
  };

  const handleClose = () => {
    setEditProviderEnrollmentrModalOpen(false);
    setIFrameUrl(null);
    form.reset();
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setAddr2Suggestions([]);
    setShowSuggestions2(false);
  };

  const payerOptions = (
    allpayer && allpayer?.payer ? allpayer?.payer : []
  ).map((p) => ({
    value: p.payerId,
    label: p?.payerName,
  }));

  const selectedPayerLabel = payerOptions.find(
    (o) => o.value === form.values.payerid
  )?.label;

  return (
    <Modal
      opened={EditProviderEnrollmentModalOpen}
      onClose={handleClose}
      withCloseButton={false}
      size={720}
      radius={12}
      padding={0}
      zIndex={1000}
      centered
      classNames={{ content: classes.modalContent, body: classes.modalBody }}
      overlayProps={{ opacity: 0.55, color: "#000" }}
    >
      {/* ── Head ── */}
      <Box className={classes.head}>
        <div>
          <Text className={classes.eyebrow}>ERA Enrollment</Text>
          <Text component="h2" className={classes.title}>
            Provider Enrollment
          </Text>
          <Text className={classes.subtitle}>
            Enroll ERA for {selectedPayerLabel || "the selected payer"}. Submit
            provider info to receive electronic remittance from this payer.
          </Text>
        </div>
        <button
          type="button"
          className={classes.closeBtn}
          onClick={handleClose}
          aria-label="Close"
        >
          <IconX />
        </button>
      </Box>

      {/* ── Body ── */}
      {!iframeurl ? (
        <>
          <Box className={classes.body}>
            <form>
              <Text component="h6" className={classes.sectionTitle}>
                Provider Information
              </Text>

              <Box
                className={classes.grid}
                style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
              >
                <Field classes={classes} label="Provider first Name" required>
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_name_f")}
                  />
                </Field>
                <Field classes={classes} label="Provider middle Name">
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_name_m")}
                  />
                </Field>
                <Field classes={classes} label="Provider last Name" required>
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_name_l")}
                  />
                </Field>
              </Box>

              <Box
                className={classes.grid}
                style={{
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  marginTop: 14,
                }}
              >
                <Field classes={classes} label="Provider NPI" required>
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_npi")}
                  />
                </Field>
                <Field classes={classes} label="Provider TaxID" required>
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_taxid")}
                  />
                </Field>
              </Box>

              <Box
                className={classes.grid}
                style={{ gridTemplateColumns: "1fr", marginTop: 14 }}
              >
                <Field classes={classes} label="Payer ID" required>
                  <Select
                    placeholder="Select Payer"
                    searchable
                    data={payerOptions}
                    styles={inputStyles}
                    rightSection={
                      <IconChevronDown color={"#94A3B8"} size={20} />
                    }
                    {...form.getInputProps("payerid")}
                  />
                </Field>
              </Box>

              <Text
                component="h6"
                className={cx(
                  classes.sectionTitle,
                  classes.sectionTitleSpaced
                )}
              >
                Address
              </Text>

              <Box
                className={classes.grid}
                style={{ gridTemplateColumns: "1fr" }}
              >
                <Field classes={classes} label="Address 1" required>
                  <Box style={{ position: "relative" }}>
                    <TextInput
                      disabled={isPreview}
                      styles={inputStyles}
                      value={form.values.prov_addr_1}
                      onChange={(e) => handleAddressSearch(e.target.value)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      rightSection={addressLoading ? <Loader size="xs" /> : null}
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <Box
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 1200,
                          background: "#fff",
                          border: "1px solid #dee2e6",
                          borderRadius: 8,
                          maxHeight: 200,
                          overflowY: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      >
                        {addressSuggestions.map((s, i) => (
                          <Box
                            key={s.place_id || i}
                            style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }}
                            sx={{ "&:hover": { background: "#f8f9fa" } }}
                            onMouseDown={() => handleSelectAddress(s)}
                          >
                            {s.formatted_address}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Field>
                <Field classes={classes} label="Address 2">
                  <Box style={{ position: "relative" }}>
                    <TextInput
                      disabled={isPreview}
                      styles={inputStyles}
                      value={form.values.prov_addr_2}
                      onChange={(e) => handleAddress2Search(e.target.value)}
                      onBlur={() => setTimeout(() => setShowSuggestions2(false), 200)}
                      onFocus={() => addr2Suggestions.length > 0 && setShowSuggestions2(true)}
                      rightSection={addr2Loading ? <Loader size="xs" /> : null}
                    />
                    {showSuggestions2 && addr2Suggestions.length > 0 && (
                      <Box style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1200, background: "#fff", border: "1px solid #dee2e6", borderRadius: 8, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                        {addr2Suggestions.map((s, i) => (
                          <Box key={s.place_id || i} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }} sx={{ "&:hover": { background: "#f8f9fa" } }} onMouseDown={() => handleSelectAddress2(s)}>
                            {s.formatted_address}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Field>
              </Box>

              <Box
                className={classes.grid}
                style={{
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  marginTop: 14,
                }}
              >
                <Field classes={classes} label="City" required>
                  <TextInput
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_city")}
                  />
                </Field>
                <Field classes={classes} label="State" required>
                  <Select
                    disabled={isPreview}
                    searchable
                    data={Array.from(
                      new Set(stateZipCodes?.map((obj) => obj.ste_name))
                    )
                      .sort()
                      .map((ste_name) => ({
                        value: ste_name,
                        label: ste_name,
                      }))}
                    styles={inputStyles}
                    rightSection={
                      <IconChevronDown color={"#94A3B8"} size={20} />
                    }
                    {...form.getInputProps("prov_state")}
                  />
                </Field>
              </Box>

              <Box
                className={classes.grid}
                style={{
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  marginTop: 14,
                }}
              >
                <Field classes={classes} label="Zip Code" required>
                  <TextInput
                    type="number"
                    disabled={isPreview}
                    styles={inputStyles}
                    {...form.getInputProps("prov_zip")}
                  />
                </Field>
                <div />
              </Box>
            </form>
          </Box>

          {/* ── Foot ── */}
          <Box className={classes.foot}>
            <Button className={classes.ghostBtn} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className={classes.accentBtn}
              onClick={onSave}
              disabled={isPreview}
              loading={loading}
            >
              Submit
            </Button>
          </Box>
        </>
      ) : (
        <Box className={classes.body}>
          <iframe
            title="Provider Enrollment"
            src={iframeurl}
            width="100%"
            height="500px"
            style={{ border: "none" }}
            onLoad={() => setLoaded(true)}
          />
        </Box>
      )}
    </Modal>
  );
};

export default ProviderEnrollment;
